import { SOLUTION_VIDEO_SOURCES, getSolutionVideoUrls } from '../components/home/solutionVideosData';

import { optimizeSolutionTabVideoUrl } from './optimizeSolutionTabVideoUrl';

import {

  applySolutionTabVideoAttrs,

  isWebKitVideoStrict,

} from './solutionTabVideoPlayback';

import { prefersReducedMotionOnly } from './shouldSkipDoorIntro';



const PRELOAD_ATTR = 'data-solution-preload';

const WARM_TIMEOUT_MS = 12000;

const warmPromises = new Map();

const warmVideos = new Map();



function normalizeHref(href) {

  return optimizeSolutionTabVideoUrl(href);

}



function addLinkPreload(href) {

  const url = normalizeHref(href);

  if (!url || document.querySelector(`link[${PRELOAD_ATTR}="${url}"]`)) return;

  const link = document.createElement('link');

  link.rel = 'preload';

  link.as = 'video';

  link.href = url;

  link.type = 'video/mp4';

  link.setAttribute(PRELOAD_ATTR, url);

  document.head.appendChild(link);

}



function warmElementStyle() {

  /* Real dimensions off-screen — Safari skips decode at 0×0 or opacity:0. */

  return 'position:fixed;width:4px;height:4px;left:-9999px;top:0;visibility:hidden;pointer-events:none;z-index:-1;overflow:hidden';

}



function createWarmElement(url, eager = false) {

  const el = document.createElement('video');

  applySolutionTabVideoAttrs(el);

  el.preload = eager ? 'auto' : 'metadata';

  el.setAttribute(PRELOAD_ATTR, url);

  el.setAttribute('aria-hidden', 'true');

  el.style.cssText = warmElementStyle();

  document.body.appendChild(el);

  warmVideos.set(url, el);

  return el;

}



/** Prime a clip into HTTP cache via link hint + hidden decoder. */

export function warmSolutionVideo(href, { eager = false } = {}) {

  const url = normalizeHref(href);

  if (!url) return Promise.resolve(null);

  const cacheKey = eager ? `${url}::eager` : url;

  if (warmPromises.has(cacheKey)) return warmPromises.get(cacheKey);



  const promise = new Promise((resolve) => {

    addLinkPreload(url);



    let el = warmVideos.get(url);

    if (!el) {

      el = createWarmElement(url, eager);

    } else if (eager && el.preload !== 'auto') {

      el.preload = 'auto';

      el.load();

    }



    let settled = false;
    let timeoutId = 0;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      resolve(value);
    };

    if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      finish(el);
      return;
    }

    const onReady = () => finish(el);
    const onError = () => finish(null);

    el.addEventListener('canplaythrough', onReady, { once: true });
    el.addEventListener('canplay', onReady, { once: true });
    el.addEventListener('loadeddata', onReady, { once: true });
    el.addEventListener('loadedmetadata', onReady, { once: true });
    el.addEventListener('error', onError, { once: true });

    timeoutId = window.setTimeout(() => finish(el), WARM_TIMEOUT_MS);



    applySolutionTabVideoAttrs(el);

    if (el.src !== url) {

      el.src = url;

      el.load();

    } else if (eager) {

      el.load();

    }

  });



  warmPromises.set(cacheKey, promise);

  return promise;

}



/** Drop hidden warm decoders when leaving home (frees GPU / memory on Safari). */

export function releaseSolutionVideoWarmers() {

  warmVideos.forEach((el) => {

    el.pause();

    el.removeAttribute('src');

    el.load();

    el.remove();

  });

  warmVideos.clear();

  warmPromises.clear();

  document.querySelectorAll(`link[${PRELOAD_ATTR}]`).forEach((node) => node.remove());

}



/** Stagger priming so mobile radios are not saturated at once. */

export function warmAllSolutionVideosStaggered(

  urls = getSolutionVideoUrls(),

  gapMs = 120

) {

  if (typeof document === 'undefined' || prefersReducedMotionOnly()) {

    return () => {};

  }



  const list = urls.map(normalizeHref).filter(Boolean);

  const timers = list.map((href, index) =>

    window.setTimeout(() => {

      warmSolutionVideo(href);

    }, index * gapMs)

  );



  return () => timers.forEach((id) => window.clearTimeout(id));

}



/** @deprecated Prefer preloadSolutionVideosForTabs — only primes the first clip. */

export function warmAllSolutionVideos(urls = getSolutionVideoUrls()) {

  const list = urls.map(normalizeHref).filter(Boolean);

  if (!list[0]) return Promise.resolve([]);

  return warmSolutionVideo(list[0]).then((el) => [el]);

}



export function isSolutionVideoWarm(href) {

  const url = normalizeHref(href);

  const el = warmVideos.get(url);

  return Boolean(el && el.readyState >= HTMLMediaElement.HAVE_METADATA);

}



export function prefetchSolutionNeedSection() {

  import('../components/home/SolutionNeedSection').catch(() => {});

}



/** Home/App: prime the first tab; on mobile also prime the second. */

export function preloadSolutionVideos(urls = getSolutionVideoUrls()) {

  if (typeof document === 'undefined' || prefersReducedMotionOnly()) return;



  const list = urls.map(normalizeHref).filter(Boolean);

  if (!list[0]) return;



  warmSolutionVideo(list[0], { eager: true });



  const isMobile =

    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

  if (isMobile && list[1]) {

    window.setTimeout(() => warmSolutionVideo(list[1], { eager: false }), 200);

  }

}



/** Warm active tab now; optionally queue the next tab after a short delay. */

export function preloadSolutionVideosForTabs(activeHref, nextHref) {

  if (typeof document === 'undefined' || prefersReducedMotionOnly()) {

    return undefined;

  }



  const touchLike =

    typeof window !== 'undefined' &&

    window.matchMedia('(pointer: coarse), (max-width: 1024px)').matches;

  const webKitStrict = isWebKitVideoStrict();



  if (activeHref) warmSolutionVideo(activeHref, { eager: true });



  if (!nextHref || normalizeHref(nextHref) === normalizeHref(activeHref)) {

    return undefined;

  }



  const primeNextEager = touchLike || webKitStrict;

  const id = window.setTimeout(

    () => warmSolutionVideo(nextHref, { eager: primeNextEager }),

    primeNextEager ? 80 : 300

  );

  return () => window.clearTimeout(id);

}



export function preloadSolutionVideoByKey(videoKey, sources = SOLUTION_VIDEO_SOURCES) {

  const href = sources[videoKey];

  if (!href) return;

  warmSolutionVideo(href);

}



export function getOptimizedSolutionVideoUrl(href) {

  return normalizeHref(href);

}

